import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { subDays } from "date-fns";
import { usePostHog } from "posthog-js/react";
import { useCallback, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router";

import { Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import type { ChatReplyPayload } from "@/components/ChatWindow/ChatWindow";
import { ChatWindow } from "@/components/ChatWindow/ChatWindow";
import { useChatReplies } from "@/components/ChatWindow/useChatReplies";
import { CommunityMessage } from "@/components/CommunityFeed/CommunityMessage";
import { CommunityPoll } from "@/components/CommunityFeed/CommunityPoll";
import { ConfirmModal } from "@/components/ConfirmModal/ConfirmModal";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { Settings01Icon } from "@/components/Icons/Settings01Icon";
import { SlashCircle01Icon } from "@/components/Icons/SlashCircle01Icon";
import { DocumentPage } from "@/components/Page/DocumentPage";
import { PageGrid } from "@/components/PageGrid/PageGrid";
import { UserDetailsContent } from "@/components/UserDetailsContent/UserDetailsContent";
import { Button } from "@/design-system/Button";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { SectionCard } from "@/design-system/SectionCard";
import { Stack } from "@/design-system/Stack";
import { addressTagsToDictionary } from "@/helpers/address";
import { canEditOtherUserNotificationSettings } from "@/helpers/permissions";
import { SIGNALR_EVENTS } from "@/helpers/signalr";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useBool } from "@/hooks/useBool";
import { usePermission } from "@/hooks/usePermission";
import { useSlug } from "@/hooks/useSlug";
import { CommunityPostViewCountTrackingProvider } from "@/providers/CommunityPostViewCountTrackingProvider";
import { useChatReplyHandler } from "@/queries/chats/helpers";
import { chatMutations } from "@/queries/chats/mutations";
import { useChatQueries } from "@/queries/chats/queryOptions";
import { useCommunityFeedQueries } from "@/queries/community-feed/queryOptions";
import { useUserQueries } from "@/queries/users/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { DeleteAdminModal } from "./components/DeleteAdminModal";
import { DeleteResidentModal } from "./components/DeleteResidentModal";

const RECENT_POSTS_DAY_COUNT = 14;
const DATE_TODAY = new Date();

export function UserDetailsPage(): React.ReactNode {
  const [isDeleteModalOpened, deleteModalOpenHandlers] = useBool(false);
  const [isConfirmBlockModalOpened, confirmBlockModalOpenHandlers] = useBool(false);
  const [refChatWindow, setRefChatWindow] = useState<HTMLDivElement | null>(null);

  const slug = useSlug();
  const { t } = useTranslation();
  const sessionUser = useSessionUser();
  const hasPermission = usePermission();
  const [queryParams, setQueryParams] = useSearchParams();
  const { id: userId } = useParams<{
    id: string;
  }>();
  const userIdForQuery = userId ?? "";
  const postHog = usePostHog();

  const chatQueries = useChatQueries();
  const userQueries = useUserQueries();
  const communityQueries = useCommunityFeedQueries();
  const {
    data: chatDetails,
    isLoading: isLoadingChatDetails,
    error: errorChatDetails,
    refetch: refetchChatDetails,
  } = useQuery({
    ...chatQueries.privateChatDetails({
      userId: userIdForQuery,
    }),
    enabled: Boolean(userId),
  });
  const chatDetailsId = chatDetails?.id;
  const isBlockedByOtherUser = chatDetails?.blockStatus?.blockedByOtherUser;
  const {
    data: receiverUser,
    isLoading: isLoadingReceiverUser,
    error: errorReceiverUser,
  } = useQuery({
    ...userQueries.details({
      userId: userIdForQuery,
    }),
    enabled: Boolean(userId),
  });
  const {
    data: repliesData,
    isLoading: isLoadingReplies,
    hasNextPage: hasMoreReplies,
    fetchNextPage: fetchMoreReplies,
    isFetchingNextPage: isLoadingMoreReplies,
    error: errorRepliesData,
  } = useInfiniteQuery({
    ...chatQueries.listChatRepliesInfinite({
      chatId: chatDetailsId || "",
    }),
    enabled: Boolean(chatDetails),
  });
  const { data: userPostsData, isPending: isPendingUserPostsData } = useQuery({
    ...communityQueries.listPosts({
      PosterId: userIdForQuery,
      StartDate: subDays(DATE_TODAY, RECENT_POSTS_DAY_COUNT).toISOString(),
      EndDate: DATE_TODAY.toISOString(),
      Limit: 50,
    }),
    enabled: Boolean(userId),
  });
  const { data: signUpReminders } = useQuery({
    ...userQueries.signUpReminders({
      userId: userIdForQuery,
    }),
    enabled: Boolean(userId),
  });
  const blockUserMutation = chatMutations.useBlockUser();
  const unblockUserMutation = chatMutations.useUnblockUser();
  const addChatMutation = chatMutations.useAddPrivateChat();
  const addChatReplyMutation = chatMutations.useAddChatReply();

  const handleReceiveNewReply = useCallback(() => {
    if (isBlockedByOtherUser) {
      void refetchChatDetails();
    }
  }, [isBlockedByOtherUser, refetchChatDetails]);

  const { chatReplies } = useChatReplies({
    chatId: chatDetailsId,
    repliesData,
    signalREvent: SIGNALR_EVENTS.CHAT.NEW_PROJECT_CHAT_REPLY_RECEIVED,
    disabled: !chatDetails,
    onReceiveNewReply: handleReceiveNewReply,
  });
  const { sendReply } = useChatReplyHandler({ addChatReplyMutation });

  // If the prefilledReply has been consumed, remove it
  useEffect(() => {
    if (queryParams.get("prefilledReply") && refChatWindow) {
      const newParams = new URLSearchParams(queryParams);

      newParams.delete("prefilledReply");
      setQueryParams(newParams, { replace: true });
    }
  }, [queryParams, setQueryParams, refChatWindow]);

  const handleAddChatReply = async ({ message, images, videos }: ChatReplyPayload) => {
    if (!userId) {
      throw new Error("Cannot send a reply without a user ID");
    }

    let chatId = chatDetails?.id;
    if (!chatId) {
      chatId = await addChatMutation.mutateAsync(userId).then((x) => x.id);
    }

    if (chatId) {
      return await sendReply({ message, images, videos, chatId });
    }

    throw new Error("Could not create a chat for this reply");
  };

  const handleUnblockUser = () => {
    if (!userId) {
      return;
    }

    postHog.capture("clicked_unblock_chat");
    unblockUserMutation.mutate(userId);
  };

  const handleBlockUser = () => {
    if (!userId) {
      return;
    }

    postHog.capture("clicked_block_chat");
    confirmBlockModalOpenHandlers.setTrue();
  };

  const error = errorReceiverUser || errorChatDetails || errorRepliesData || !userId;
  if (error) {
    return <ErrorPage error={error} />;
  }

  const isLoading = isLoadingReplies || isLoadingReceiverUser || isLoadingChatDetails;
  if (isLoading) {
    return <FullSizeLoader withPadding />;
  }

  if (!receiverUser) {
    return <ErrorPage status={404} />;
  }

  const canReinvite =
    !receiverUser.registeredAt &&
    (signUpReminders?.canBeReminded || sessionUser.isSuperAdmin) &&
    ((receiverUser.isAdmin && hasPermission((x) => x.userManagement.canEditAdminInfo)) ||
      (!receiverUser.isAdmin && hasPermission((x) => x.userManagement.canEditResidentInfo)));
  const canBlockUser =
    userId !== sessionUser.id &&
    chatDetails &&
    !receiverUser.isAdmin &&
    !receiverUser.deletedAt &&
    !chatDetails?.blockStatus?.blockedByRequester;
  const canUnblockUser =
    chatDetails && !receiverUser.isAdmin && !receiverUser.deletedAt && chatDetails?.blockStatus?.blockedByRequester;
  const canDeleteUser = receiverUser.canBeDeleted;
  const canEditNotificationSettings =
    hasPermission(canEditOtherUserNotificationSettings) && receiverUser.isAdmin && !receiverUser.deletedAt;
  // If no chat history, the ability to start a new chat depends on both users' chat permissions (chat blockage can be ignored as there is no chat yet)
  const canStartNewChat =
    sessionUser.chatPermission !== "disabled" && receiverUser.chatPermission === "enabled" && !receiverUser.deletedAt;
  const canSendChatReply = chatDetails ? chatDetails.canReply : canStartNewChat;
  const chatName = receiverUser.fullName;
  const userPosts = userPostsData?.items ?? [];

  return (
    <>
      <DocumentPage
        containerBoundary="inherit"
        tabTitle={receiverUser.fullName}
        breadcrumbs={
          <Breadcrumbs
            pages={[
              {
                name: t("page.user-overview.title"),
                to: routes.users.list({
                  slug,
                }),
              },
              {
                name: chatName,
              },
            ]}
          />
        }
        actions={
          <>
            {canBlockUser && (
              <Button
                data-testid="block-user-private-chat-btn"
                variant="soft"
                icon={<SlashCircle01Icon />}
                onClick={handleBlockUser}
              >
                {t("page.user-detail.actions.block-private-chat-from-user")}
              </Button>
            )}
            {canUnblockUser && (
              <Button
                data-testid="unblock-user-private-chat-btn"
                variant="soft"
                icon={<SlashCircle01Icon />}
                onClick={handleUnblockUser}
              >
                {t("page.user-detail.actions.unblock-private-chat-from-user")}
              </Button>
            )}
            {canEditNotificationSettings && (
              <Button
                type="link"
                data-testid="notification-settings-btn"
                href={routes.adminNotifications.otherUserSettings({
                  slug,
                  userId: userId,
                })}
                onClick={() => postHog.capture("clicked_edit_user_notification_settings_from_details_page")}
                state={{
                  fromPage: routes.users.details({
                    id: userId,
                    slug,
                  }),
                }}
                variant="soft"
                icon={<Settings01Icon />}
              >
                {t("page.user-detail.actions.edit-user-notifications")}
              </Button>
            )}
            {canDeleteUser && (
              <Button
                data-testid="delete-user-btn"
                variant="filled"
                tone="error"
                onClick={() => {
                  postHog.capture("clicked_delete_user_from_details_page");
                  deleteModalOpenHandlers.setTrue();
                }}
              >
                {t("page.user-detail.actions.delete-user")}
              </Button>
            )}
            {receiverUser.canBeEdited && (
              <Button
                type="link"
                data-testid="edit-user-btn"
                href={routes.users.edit({
                  slug,
                  id: receiverUser.id,
                })}
              >
                {t("page.user-detail.actions.edit-user-details")}
              </Button>
            )}
          </>
        }
      >
        <PageGrid.Row>
          <PageGrid.Column size="66%" sizeFrom="sm">
            <section className="flex w-full grow flex-col gap-space-lg">
              {sessionUser.id !== userId && (
                <div className="max-h-[640px] flex-1" ref={setRefChatWindow}>
                  <ChatWindow
                    canSendChatReply={canSendChatReply}
                    chatReplies={chatReplies}
                    hasMoreReplies={hasMoreReplies}
                    statuses={{
                      isDisabledByReceiver:
                        receiverUser.chatPermission === "disabled" || receiverUser.chatPermission === "sendOnly",
                      isDisabledBySender: sessionUser.chatPermission === "disabled",
                      isBlockedBySender: chatDetails?.blockStatus?.blockedByRequester,
                      isBlockedByReceiver: chatDetails?.blockStatus?.blockedByOtherUser,
                    }}
                    fetchMoreReplies={fetchMoreReplies}
                    isLoadingMoreReplies={isLoadingMoreReplies}
                    sendReply={handleAddChatReply}
                    prefilledReply={queryParams.get("prefilledReply") ?? undefined}
                  />
                </div>
              )}
              <Stack gap="sm">
                {isPendingUserPostsData && <FullSizeLoader size="sm" />}
                {!isPendingUserPostsData && (
                  <>
                    <span className="text-caption-emphasis">
                      {t("page.user-detail.recent-posts.title", { dayCount: RECENT_POSTS_DAY_COUNT })}
                    </span>
                    {userPosts.length > 0 && (
                      <CommunityPostViewCountTrackingProvider>
                        <Stack gap="md">
                          {userPosts.map((currPost) => {
                            if (currPost.message) {
                              return (
                                <CommunityMessage key={`message-${currPost.message.id}`} message={currPost.message} />
                              );
                            }

                            if (currPost.poll) {
                              return <CommunityPoll key={`poll-${currPost.poll.id}`} poll={currPost.poll} />;
                            }

                            return null;
                          })}
                        </Stack>
                      </CommunityPostViewCountTrackingProvider>
                    )}
                    {userPosts.length == 0 && <span className="text-caption">{t("page.user-detail.no-posts")}</span>}
                  </>
                )}
              </Stack>
            </section>
          </PageGrid.Column>
          <PageGrid.Column size="33%" sizeFrom="sm">
            <SectionCard>
              <UserDetailsContent
                user={receiverUser}
                address={addressTagsToDictionary(receiverUser.addressTags)}
                canReinvite={canReinvite}
                showOtherProjects={sessionUser.isSuperAdmin}
                showPersonalDetails
                showAddressDetails
                showContactDetails
                showPlatformDetails
                showGroups
                showIntegrations
              />
            </SectionCard>
          </PageGrid.Column>
        </PageGrid.Row>
      </DocumentPage>

      <ConfirmModal
        tone="warning"
        data-testid="confirm-block-private-chat-modal"
        title={t("page.user-detail.block-private-chat-modal.title")}
        description={t("page.user-detail.block-private-chat-modal.description")}
        onResolve={() =>
          blockUserMutation.mutate(userId, {
            onSettled: () => confirmBlockModalOpenHandlers.setFalse(),
          })
        }
        onReject={confirmBlockModalOpenHandlers.setFalse}
        resolveBtnProps={{
          text: t("common.action.confirm"),
          "data-testid": "confirm-btn",
        }}
        isOpened={isConfirmBlockModalOpened}
        onOpenChange={confirmBlockModalOpenHandlers.set}
        isLoading={false}
      />
      <DeleteAdminModal
        user={receiverUser}
        isOpened={isDeleteModalOpened && Boolean(receiverUser?.isAdmin)}
        onOpenChange={deleteModalOpenHandlers.set}
      />
      <DeleteResidentModal
        user={receiverUser}
        isOpened={isDeleteModalOpened && !receiverUser?.isAdmin}
        onOpenChange={deleteModalOpenHandlers.set}
      />
    </>
  );
}
